import React, { useEffect, useState } from 'react';
import { Loading } from 'aha-components';
import { GoogleIcon1 } from '@/components/common/icon';

interface AppAuthProgressProps {
  compact?: boolean;
  tone?: 'default' | 'google';
}

const SLOW_HINT_DELAY_MS = 4000;

const AppAuthProgress: React.FC<AppAuthProgressProps> = ({
  compact = false,
  tone = 'default'
}) => {
  const [showSlowHint, setShowSlowHint] = useState(false);

  useEffect(() => {
    if (tone !== 'google') return;
    const timer = window.setTimeout(
      () => setShowSlowHint(true),
      SLOW_HINT_DELAY_MS
    );
    return () => window.clearTimeout(timer);
  }, [tone]);

  if (tone !== 'google') {
    return (
      <div
        className={`flex flex-col items-center justify-center px-6 text-center text-gray-500 ${
          compact ? 'min-h-[60vh] w-full' : 'min-h-[100dvh] w-full'
        }`}
      >
        <Loading width={50} height={50} color="#000" />
      </div>
    );
  }

  return (
    <div
      className={`relative flex flex-col items-center justify-center bg-white px-6 ${
        compact ? 'min-h-[60vh] w-full' : 'min-h-[100dvh] w-full'
      }`}
      role="status"
      aria-label="Signing in with Google"
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px] overflow-hidden bg-[#F1F1EF]"
        aria-hidden
      >
        <div
          className="absolute inset-y-0 left-0 w-full origin-left bg-[#1A73E8]"
          style={{
            animation:
              'app-auth-progress-1 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite'
          }}
        />
        <div
          className="absolute inset-y-0 left-0 w-full origin-left bg-[#1A73E8]"
          style={{
            animation:
              'app-auth-progress-2 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite',
            animationDelay: '1.15s'
          }}
        />
      </div>

      <div className="flex flex-col items-center gap-7">
        <div className="flex items-center justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-[#E7E5E4] bg-white shadow-sm">
            <img
              className="h-11 w-11"
              src="https://website-resource.ahaglobal.io/aha-brand-logo/aha-logo-black.png"
              alt=""
            />
          </div>
          <div className="mx-4 h-px w-10 bg-[#D6D3D1]" aria-hidden />
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-[#E7E5E4] bg-white shadow-sm">
            <GoogleIcon1 className="h-11 w-11" />
          </div>
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="text-[15px] font-medium leading-6 text-[#44403C]">
            Signing in with Google
          </div>
          <div
            className={`max-w-[300px] text-[13px] font-normal leading-5 text-[#78716C] transition-opacity duration-500 ease-out ${
              showSlowHint ? 'opacity-100' : 'opacity-0'
            }`}
            aria-live="polite"
          >
            This may take a few seconds
            <span aria-hidden>
              <span>.</span>
              <span
                style={{
                  animation: 'app-auth-dot-2 1.5s steps(1, end) infinite'
                }}
              >
                .
              </span>
              <span
                style={{
                  animation: 'app-auth-dot-3 1.5s steps(1, end) infinite'
                }}
              >
                .
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppAuthProgress;
